/**
 * @file: VehicleServiceRecord.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { IdType, Range } from '@euler/model/common';
import {
  AbnormalLevel,
  CarwashType,
  CommonTaskStatus,
  CustomerType,
  InspectionTaskEventDataType,
  InspectionTaskEventSubType,
  InspectionTaskEventType,
  InspectionTaskTroubleCodeState,
  OldPartDisposalType,
  OrderReservationType,
  OrderReturnType,
  SamplingCheckResultType,
  SeverityLevel,
  SiteInspectionType,
  TaskProductManifestItemOrigin,
} from '@euler/model/enum';
import {
  FaultLikeSubjectRelatedConstructionJobInfo,
  FaultLikeSubjectRelatedInspectionInfo,
  OrderDetail,
} from '@euler/model/OrderDetail';
import { ProductAuthorInfo, ProductInfo } from '@euler/model/product';
import { PendingIssue } from '@euler/model/report';
import { ReportShareSummary } from '@euler/model/ReportShareSummary';
import { ReportViewSummary } from '@euler/model/ReportViewSummary';
import {
  ConstructionJobProductConfirmationItem,
  QuotationForm,
  SettlementForm,
} from '@euler/model/task-detail';
import { VehicleBindingCriterion } from '@euler/model/VehicleBindingCriterion';
import { Maybe } from '@euler/types';

export type VehicleServiceItemSource = {
  orderNo: string;
  reportNo: string;
  taskNo: string;
};

export enum VehicleServiceRecordType {
  System = 'system',
  User = 'user',
}

export interface VehicleServiceItem {
  name: string;
  type: 'inspection-flow' | 'job' | 'manually-solved-pending-issue';
  label?: Maybe<string>;
  remark?: Maybe<string>;
  author: Maybe<string>;
  groupName?: Maybe<string>;
  teamName?: Maybe<string>;
}

export interface VehicleFaultLikeServiceItem {
  type: 'fault' | 'other';
  name: string;
  solved: boolean;
  remark?: Maybe<string>;
  source?: VehicleServiceItemSource;
  relatedInspections?: FaultLikeSubjectRelatedInspectionInfo[];
  relatedJobs?: FaultLikeSubjectRelatedConstructionJobInfo[];
}

export interface VehicleServiceEvent {
  id: string;
  type: InspectionTaskEventType;
  subType: InspectionTaskEventSubType;
  timestamp: Date;
  data: string | null;
  dataType: InspectionTaskEventDataType;
  dataVersion: number;
  author: string | null;
}

export interface VehicleServiceTroubleCode {
  sourceModule: string;
  sourceModuleName: string;
  code: string | null;
  description: string | null;
  status: InspectionTaskTroubleCodeState | null;
  statusText: string | null;
}

export interface VehicleServicePendingIssue {
  title: string;
  abnormalLevel: AbnormalLevel;
  severityLevel: SeverityLevel;
  inspectionType: SiteInspectionType;
  siteId: number | null;
  siteName: string | null;
  siteCode: string | null;
  itemName: string | null;
  itemId: number | null;
  customIssueId: number | null;
  maintenanceAdvice: string | null;
}

export interface VehicleServiceRecordUserMedia {
  id: string;
  type: string;
  label: string | null;
  url: string;
  coverUrl: string;
  tag: string | null;
}

export interface VehicleServiceComment {
  score: number;
  serviceAttitudeScore: number;
  serviceEfficiencyScore: number;
  professionalDegreeScore: number;
  satisfactionDegreeScore: number;
  comment: string | null;
  timestamp: Date;
}

export interface CustomVehicleServiceRecordInfo {
  serviceDate: string;
  licensePlateNo: string;
  vin: string;
  storeName: string;
  mileage: number;
  totalCost?: number | null;
  items: VehicleServiceItem[];
  userMedias: VehicleServiceRecordUserMedia[];
}

export type ServiceRecordProductReferralItemConfirmation =
  ConstructionJobProductConfirmationItem & {
    jobId: number;
    subject: string;
  };

export type ServiceRecordProductInfo = ProductInfo & {
  sourceContractId: IdType;
};

export type ServiceRecordProductReferralItem = {
  id: IdType;
  subject: string;
  product: ServiceRecordProductInfo;
  qty: number | undefined;
  author: ProductAuthorInfo;
  origin: TaskProductManifestItemOrigin | undefined;
  jobRefs: number[] | undefined;
  confirmations: ServiceRecordProductReferralItemConfirmation[] | undefined;
};

export interface VehicleServiceRecord {
  _id: string;
  isMainRecord: boolean;
  isFirstTimeOrder: boolean | null;
  hasChildRecords: boolean | undefined;
  type: VehicleServiceRecordType;
  licensePlateNo: string;
  vin: string;
  orgId: number;
  storeId: number;
  organizationName: string;
  storeName: string;
  serviceAgentName: string;
  orderNo: string;
  taskNo: string;
  reportNo: string;
  vehicleName: string;
  vehicleBrand: string;
  vehicleManufacturer: string | null;
  vehicleModel: string | null;
  vehicleModelYear: number | null;
  vehicleDisplacement: string | null;
  vehicleFuelType: string | null;
  vehicleTrimLevel: string | null;
  mileage: number;
  serviceTime: Date;
  orderedAt: Date;
  finishedAt: Date;
  createdAt: Date;
  tags: Maybe<string[]>;
  customerName: Maybe<string>;
  customerMobile: Maybe<string>;
  customerIsOnSite: Maybe<boolean>;
  customerType: Maybe<CustomerType>;
  carwash: Maybe<boolean>;
  carwashType: Maybe<CarwashType>;
  returnType: Maybe<OrderReturnType>;
  oldPartDisposalType: Maybe<OldPartDisposalType>;
  reservationType: Maybe<OrderReservationType>;
  insuranceExpiryDate: Maybe<string | Date>;
  subscribed: Maybe<boolean>;
  dashboardImgUrl: Maybe<string>;
  signImgUrl: Maybe<string>;
  preInspectionStatus: Maybe<CommonTaskStatus>;
  inspectionStatus: Maybe<CommonTaskStatus>;
  constructionStatus: Maybe<CommonTaskStatus>;
  deliveryCheckStatus: Maybe<CommonTaskStatus>;
  samplingCheckStatus: Maybe<CommonTaskStatus>;
  samplingCheckResult: Maybe<SamplingCheckResultType>;
  quotationStatus: Maybe<CommonTaskStatus>;
  settlementStatus: Maybe<CommonTaskStatus>;
  quotationFormId: Maybe<IdType>;
  settlementFormId: Maybe<IdType>;
  quotationFormNo?: Maybe<string>;
  settlementFormNo?: Maybe<string>;
  isObdInspected: Maybe<boolean>;
  isConstructionJobScheduled: Maybe<boolean>;
  orderDetail: Maybe<OrderDetail>;
  items: VehicleServiceItem[] | null;
  faultItems: VehicleFaultLikeServiceItem[] | null;
  events: VehicleServiceEvent[] | null;
  userMedias: VehicleServiceRecordUserMedia[] | null;
  troubleCodes: VehicleServiceTroubleCode[] | null;
  pendingIssuesV2: PendingIssue[];
  productReferrals: Maybe<ServiceRecordProductReferralItem[]>;
  pendingIssueCount: Maybe<number>;
  maintenancePlanItemCount: Maybe<number>;
  remark: string | null;
  totalCost: number | null;
  comment: VehicleServiceComment | null;
  reportViewSummary: Maybe<ReportViewSummary>;
  reportShareSummary: Maybe<ReportShareSummary>;
  authors: Maybe<number[]>;
  quotationForm: Maybe<QuotationForm>;
  settlementForm: Maybe<SettlementForm>;
}

export interface VehicleServiceRecordFilter {
  type: VehicleServiceRecordType;
  licensePlateNo: string;
  vehicleBindings: VehicleBindingCriterion[];
  vin: string;
  orgId: number;
  storeId: number;
  orderNo: string;
  taskNo: string;
  reportNo: string;
  mileage: number;
  mileageRange: Range<number>;
  serviceTime: Range<string | Date>;
  orderedAt: Range<string | Date>;
  finishedAt: Range<string | Date>;
  createdAt: Range<string | Date>;
}

export interface VehicleServiceRecordProjection {
  items: boolean;
  events: boolean;
  userMedias: boolean;
  troubleCodes: boolean;
  pendingIssues: boolean;
  comment: boolean;
}
